import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  LuArrowLeft,
  LuArrowRight,
  LuClock,
  LuDownload,
  LuFileText,
  LuFlag,
  LuLayers,
  LuMail,
  LuMessageSquare,
  LuPackage,
  LuPaperclip,
  LuPhone,
  LuPlus,
  LuStar,
} from "react-icons/lu";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import {
  fetchMySupportTicketById,
  postMySupportTicketMessage,
  ticketExtractMessage,
} from "../../services/supportTicket.service";
import "../../assets/styles/my_dispute_detail.scss";

const TIMELINE_STEPS = [
  { key: "submitted", label: "Submitted", match: ["new"] },
  {
    key: "under_review",
    label: "Under Review",
    match: ["assigned", "under_investigation", "waiting_customer"],
  },
  { key: "in_progress", label: "In Progress", match: ["in_progress"] },
  { key: "resolved", label: "Resolved", match: ["resolved"] },
  { key: "closed", label: "Closed", match: ["closed"] },
];

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatStatus(status, label) {
  if (label) return label;
  return String(status || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function nameInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function statusClass(status) {
  const normalized = String(status || "").toLowerCase();
  return `mdd-status mdd-status--${normalized.replace(/\s+/g, "_")}`;
}

function priorityClass(priority) {
  const normalized = String(priority || "").toLowerCase();
  return `mdd-priority mdd-priority--${normalized}`;
}

function isImageMime(mime, name = "") {
  if (mime && String(mime).startsWith("image/")) return true;
  return /\.(jpg|jpeg|png|gif|webp)$/i.test(String(name || ""));
}

function currentTimelineIndex(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "reopened") return 2;
  if (normalized === "closed") return 4;
  if (normalized === "resolved") return 3;
  if (normalized === "in_progress") return 2;
  if (["assigned", "under_investigation", "waiting_customer"].includes(normalized)) return 1;
  if (normalized === "new") return 0;
  return 0;
}

function collectAttachments(ticketAttachments, messages) {
  const items = [];
  (ticketAttachments || []).forEach((file) => {
    items.push({
      id: `att-${file.id}`,
      name: file.fileName || "Attachment",
      path: file.filePath,
      mime: file.fileMime,
      createdAt: file.createdAt,
    });
  });
  (messages || []).forEach((msg) => {
    if (msg.attachmentPath || msg.attachmentName) {
      items.push({
        id: `msg-${msg.id}`,
        name: msg.attachmentName || "Attachment",
        path: msg.attachmentPath,
        mime: null,
        createdAt: msg.createdAt,
      });
    }
  });
  return items;
}

export default function MyTicketDetailPage() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const composerRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [message, setMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [showAllAttachments, setShowAllAttachments] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMySupportTicketById(ticketId);
      if (!data?.ticket) throw new Error("Ticket not found.");
      setDetail(data);
    } catch (error) {
      toast.error(ticketExtractMessage(error, "Failed to load ticket details."));
      navigate("/dashboard/help-center/my-tickets");
    } finally {
      setLoading(false);
    }
  }, [navigate, ticketId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [detail?.messages]);

  const ticket = detail?.ticket;
  const messages = useMemo(
    () => (Array.isArray(detail?.messages) ? detail.messages : []),
    [detail?.messages],
  );
  const activities = useMemo(
    () => (Array.isArray(detail?.activities) ? detail.activities : []),
    [detail?.activities],
  );
  const attachments = useMemo(
    () => collectAttachments(detail?.attachments, messages),
    [detail?.attachments, messages],
  );
  const visibleAttachments = showAllAttachments ? attachments : attachments.slice(0, 2);
  const activeStep = currentTimelineIndex(ticket?.status);
  const activityItems = showAllActivity ? activities : activities.slice(0, 5);

  const onSendMessage = async (event) => {
    event.preventDefault();
    if (!message.trim() && !attachment) {
      toast.error("Enter a message or attach a file.");
      return;
    }
    setSending(true);
    try {
      const data = await postMySupportTicketMessage(ticketId, {
        message: message.trim(),
        attachment,
      });
      setDetail(data);
      setMessage("");
      setAttachment(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.success("Message sent.");
    } catch (error) {
      toast.error(ticketExtractMessage(error, "Failed to send message."));
    } finally {
      setSending(false);
    }
  };

  const focusComposer = () => {
    composerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => composerRef.current?.querySelector("textarea")?.focus(), 200);
  };

  const downloadDetails = () => {
    if (!ticket) return;
    try {
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const marginX = 40;
      let cursorY = 48;

      const ensureSpace = (needed = 40) => {
        const pageHeight = doc.internal.pageSize.getHeight();
        if (cursorY + needed > pageHeight - 40) {
          doc.addPage();
          cursorY = 48;
        }
      };

      const addSectionTitle = (title) => {
        ensureSpace(36);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(15, 28, 51);
        doc.text(title, marginX, cursorY);
        cursorY += 10;
        doc.setDrawColor(226, 232, 240);
        doc.line(marginX, cursorY, pageWidth - marginX, cursorY);
        cursorY += 14;
      };

      const addWrappedText = (label, value, options = {}) => {
        const text = String(value || "—");
        const maxWidth = pageWidth - marginX * 2;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(102, 112, 133);
        ensureSpace(18);
        doc.text(label, marginX, cursorY);
        cursorY += 12;
        doc.setFont("helvetica", options.boldValue ? "bold" : "normal");
        doc.setFontSize(10);
        doc.setTextColor(15, 28, 51);
        const lines = doc.splitTextToSize(text, maxWidth);
        ensureSpace(lines.length * 13 + 8);
        doc.text(lines, marginX, cursorY);
        cursorY += lines.length * 13 + 8;
      };

      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.setTextColor(15, 28, 51);
      doc.text("Support Ticket Details", marginX, cursorY);
      cursorY += 18;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(102, 112, 133);
      doc.text(`Generated on ${formatDateTime(new Date().toISOString())}`, marginX, cursorY);
      cursorY += 22;

      addSectionTitle("Ticket Summary");
      autoTable(doc, {
        startY: cursorY,
        margin: { left: marginX, right: marginX },
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 6, textColor: [15, 28, 51] },
        headStyles: { fillColor: [245, 247, 250], textColor: [102, 112, 133], fontStyle: "bold" },
        columnStyles: { 0: { cellWidth: 140, fontStyle: "bold" } },
        body: [
          ["Ticket ID", ticket.ticketNumber || "—"],
          ["Status", formatStatus(ticket.status, ticket.statusLabel)],
          ["Priority", ticket.priority || "—"],
          ["Category", ticket.categoryLabel || "—"],
          ["Subject", ticket.subject || "—"],
          ["Affected Module", ticket.affectedModule || "—"],
          ["Reference Number", ticket.referenceNumber || "—"],
          ["SLA", ticket.slaRemainingLabel || ticket.expectedResponseLabel || "—"],
          ["Submitted On", formatDateTime(ticket.createdAt)],
          ["Last Updated", formatDateTime(ticket.updatedAt)],
          [
            "Assignment",
            ticket.assignedToName
              ? `Assigned · ${ticket.assignedToName}`
              : ticket.assignmentType === "unassigned"
                ? "Unassigned"
                : formatStatus(ticket.assignmentType),
          ],
        ],
      });
      cursorY = (doc.lastAutoTable?.finalY || cursorY) + 18;

      addSectionTitle("Description");
      addWrappedText("Details", ticket.description || "No description provided.");

      addSectionTitle("Customer");
      autoTable(doc, {
        startY: cursorY,
        margin: { left: marginX, right: marginX },
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 6, textColor: [15, 28, 51] },
        headStyles: { fillColor: [245, 247, 250], textColor: [102, 112, 133], fontStyle: "bold" },
        columnStyles: { 0: { cellWidth: 140, fontStyle: "bold" } },
        body: [
          ["Name", ticket.customerName || "—"],
          ["Email", ticket.customerEmail || "—"],
          [
            "Phone",
            [ticket.customerCountryCode, ticket.customerPhone].filter(Boolean).join(" ") || "—",
          ],
        ],
      });
      cursorY = (doc.lastAutoTable?.finalY || cursorY) + 18;

      addSectionTitle("Support Contact");
      autoTable(doc, {
        startY: cursorY,
        margin: { left: marginX, right: marginX },
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 6, textColor: [15, 28, 51] },
        headStyles: { fillColor: [245, 247, 250], textColor: [102, 112, 133], fontStyle: "bold" },
        columnStyles: { 0: { cellWidth: 140, fontStyle: "bold" } },
        body: [
          ["Assignee", ticket.assignedToName || "Awaiting assignment"],
          ["Assignee Email", ticket.assignedToEmail || "—"],
          ["Manager", ticket.managerName || "—"],
        ],
      });
      cursorY = (doc.lastAutoTable?.finalY || cursorY) + 18;

      addSectionTitle("Conversation");
      if (!messages.length) {
        addWrappedText("Messages", "No messages yet.");
      } else {
        autoTable(doc, {
          startY: cursorY,
          margin: { left: marginX, right: marginX },
          theme: "striped",
          styles: { fontSize: 8, cellPadding: 5, textColor: [15, 28, 51], overflow: "linebreak" },
          headStyles: { fillColor: [37, 99, 235], textColor: [255, 255, 255], fontStyle: "bold" },
          head: [["From", "Message", "Attachment", "Sent At"]],
          body: messages.map((msg) => [
            msg.senderName || msg.senderType || "—",
            msg.messageBody || "—",
            msg.attachmentName || "—",
            formatDateTime(msg.createdAt),
          ]),
          columnStyles: {
            0: { cellWidth: 90 },
            1: { cellWidth: 220 },
            2: { cellWidth: 90 },
            3: { cellWidth: 95 },
          },
        });
        cursorY = (doc.lastAutoTable?.finalY || cursorY) + 18;
      }

      addSectionTitle("Activity History");
      if (!activities.length) {
        addWrappedText("Activities", "No activity recorded.");
      } else {
        autoTable(doc, {
          startY: cursorY,
          margin: { left: marginX, right: marginX },
          theme: "striped",
          styles: { fontSize: 8, cellPadding: 5, textColor: [15, 28, 51], overflow: "linebreak" },
          headStyles: { fillColor: [37, 99, 235], textColor: [255, 255, 255], fontStyle: "bold" },
          head: [["Title", "Remarks", "By", "When"]],
          body: activities.map((item) => [
            item.title || "—",
            item.remarks || "—",
            item.performedByName || item.performedByType || "—",
            formatDateTime(item.createdAt),
          ]),
        });
        cursorY = (doc.lastAutoTable?.finalY || cursorY) + 18;
      }

      addSectionTitle("Attachments");
      if (!attachments.length) {
        addWrappedText("Files", "No attachments.");
      } else {
        autoTable(doc, {
          startY: cursorY,
          margin: { left: marginX, right: marginX },
          theme: "striped",
          styles: { fontSize: 8, cellPadding: 5, textColor: [15, 28, 51] },
          headStyles: { fillColor: [37, 99, 235], textColor: [255, 255, 255], fontStyle: "bold" },
          head: [["File Name", "Uploaded At"]],
          body: attachments.map((file) => [file.name || "—", formatDateTime(file.createdAt)]),
        });
      }

      const filename = `${ticket.ticketNumber || "ticket"}-details.pdf`;
      doc.save(filename);
      toast.success("Ticket details downloaded as PDF.");
    } catch (error) {
      toast.error(ticketExtractMessage(error, "Failed to download PDF."));
    }
  };

  if (loading || !ticket) {
    return (
      <div className="mdd-page">
        <p className="mdd-loading">Loading ticket details...</p>
      </div>
    );
  }

  const customerPhone = [ticket.customerCountryCode, ticket.customerPhone]
    .filter(Boolean)
    .join(" ")
    .trim();
  const referenceDisplay = [ticket.affectedModule, ticket.referenceNumber].filter(Boolean).join(" · ") || "—";

  return (
    <div className="mdd-page">
      <nav className="mdd-breadcrumb" aria-label="Breadcrumb">
        <Link to="/home">Home</Link>
        <span aria-hidden>&gt;</span>
        <Link to="/help-center">Help Center</Link>
        <span aria-hidden>&gt;</span>
        <Link to="/dashboard/help-center/my-tickets">My Tickets</Link>
        <span aria-hidden>&gt;</span>
        <span aria-current="page">Ticket Details</span>
      </nav>

      <Link to="/dashboard/help-center/my-tickets" className="mdd-back">
        <LuArrowLeft aria-hidden /> Back to My Tickets
      </Link>

      <header className="mdd-head">
        <h1>Ticket Details</h1>
        <div className="mdd-head__actions">
          <button type="button" className="mdd-btn mdd-btn--outline" onClick={downloadDetails}>
            <LuDownload aria-hidden /> Download Details
          </button>
          <button type="button" className="mdd-btn mdd-btn--primary" onClick={focusComposer}>
            <LuPlus aria-hidden /> Add Message
          </button>
        </div>
      </header>

      <section className="mdd-meta">
        <div className="mdd-meta__item">
          <span>Status</span>
          <strong className={statusClass(ticket.status)}>
            {formatStatus(ticket.status, ticket.statusLabel)}
          </strong>
        </div>
        <div className="mdd-meta__item">
          <span>Ticket ID</span>
          <strong>{ticket.ticketNumber}</strong>
        </div>
        <div className="mdd-meta__item">
          <span>Reference</span>
          <strong>{referenceDisplay}</strong>
        </div>
        <div className="mdd-meta__item">
          <span>Submitted On</span>
          <strong>{formatDateTime(ticket.createdAt)}</strong>
        </div>
        <div className="mdd-meta__item">
          <span>Last Updated</span>
          <strong>{formatDateTime(ticket.updatedAt)}</strong>
        </div>
      </section>

      <div className="mdd-layout">
        <div className="mdd-main">
          <section className="mdd-card">
            <header className="mdd-card__head">
              <div>
                <h2>Ticket Summary</h2>
                <p>Here is a summary of your support ticket.</p>
              </div>
            </header>
            <div className="mdd-summary-grid">
              <article>
                <span className="mdd-summary-grid__icon mdd-summary-grid__icon--blue">
                  <LuPackage aria-hidden />
                </span>
                <div>
                  <small>Category</small>
                  <strong>{ticket.categoryLabel || "—"}</strong>
                </div>
              </article>
              <article>
                <span className="mdd-summary-grid__icon mdd-summary-grid__icon--red">
                  <LuFlag aria-hidden />
                </span>
                <div>
                  <small>Priority</small>
                  <strong className={priorityClass(ticket.priority)}>{ticket.priority || "—"}</strong>
                </div>
              </article>
              <article>
                <span className="mdd-summary-grid__icon mdd-summary-grid__icon--purple">
                  <LuFileText aria-hidden />
                </span>
                <div>
                  <small>Subject</small>
                  <strong>{ticket.subject || "—"}</strong>
                </div>
              </article>
              <article>
                <span className="mdd-summary-grid__icon mdd-summary-grid__icon--green">
                  <LuClock aria-hidden />
                </span>
                <div>
                  <small>SLA</small>
                  <strong>{ticket.slaRemainingLabel || ticket.expectedResponseLabel || "—"}</strong>
                </div>
              </article>
            </div>
            <div className="mdd-description">
              <h3>Description</h3>
              <p>{ticket.description || "No description provided."}</p>
            </div>
          </section>

          <section className="mdd-card mdd-order-card">
            <div className="mdd-order-card__grid">
              <div>
                <h2>Ticket Details</h2>
                <dl className="mdd-dl">
                  <div>
                    <dt>Affected Module</dt>
                    <dd>{ticket.affectedModule || "—"}</dd>
                  </div>
                  <div>
                    <dt>Reference Number</dt>
                    <dd>{ticket.referenceNumber || "—"}</dd>
                  </div>
                  <div>
                    <dt>Expected Response</dt>
                    <dd>{ticket.expectedResponseLabel || "—"}</dd>
                  </div>
                  <div>
                    <dt>Assignment</dt>
                    <dd>
                      {ticket.assignedToName
                        ? `Assigned · ${ticket.assignedToName}`
                        : ticket.assignmentType === "unassigned"
                          ? "Unassigned"
                          : formatStatus(ticket.assignmentType)}
                    </dd>
                  </div>
                </dl>
              </div>
              <div className="mdd-parties">
                <div>
                  <h3>Submitted By</h3>
                  <div className="mdd-person">
                    <span className="mdd-avatar mdd-avatar--buyer">
                      {nameInitials(ticket.customerName)}
                    </span>
                    <div>
                      <strong>{ticket.customerName || "—"}</strong>
                      <span>{ticket.customerEmail || "—"}</span>
                      <span>{customerPhone || "—"}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3>Support Contact</h3>
                  <div className="mdd-person">
                    <span className="mdd-avatar mdd-avatar--support">
                      {nameInitials(ticket.assignedToName || "Support")}
                    </span>
                    <div>
                      <strong>{ticket.assignedToName || "Awaiting assignment"}</strong>
                      <span>{ticket.assignedToEmail || "Support Team"}</span>
                      <span>{ticket.managerName ? `Manager: ${ticket.managerName}` : "—"}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mdd-card mdd-conversation" ref={composerRef}>
            <header className="mdd-card__head">
              <div>
                <h2>Conversation</h2>
                <p>Communicate with our support team about this ticket.</p>
              </div>
            </header>

            <div className="mdd-chat">
              {messages.length === 0 ? (
                <p className="mdd-chat__empty">No messages yet. Start the conversation below.</p>
              ) : (
                messages.map((msg) => {
                  const isSupport =
                    msg.senderType === "support" ||
                    msg.senderType === "system" ||
                    msg.senderType === "manager";
                  const isMine = msg.senderType === "customer";
                  return (
                    <article
                      key={msg.id}
                      className={`mdd-bubble ${
                        isSupport ? "mdd-bubble--support" : isMine ? "mdd-bubble--mine" : "mdd-bubble--other"
                      }`}
                    >
                      <span
                        className={`mdd-avatar ${isSupport ? "mdd-avatar--support" : "mdd-avatar--buyer"}`}
                      >
                        {nameInitials(msg.senderName)}
                      </span>
                      <div className="mdd-bubble__body">
                        <header>
                          <strong>
                            {msg.senderName || (isSupport ? "Support Team" : "You")}
                          </strong>
                          <time>{formatDateTime(msg.createdAt)}</time>
                        </header>
                        <p>{msg.messageBody}</p>
                        {msg.attachmentPath ? (
                          isImageMime(null, msg.attachmentName) ? (
                            <a
                              className="mdd-chat-image"
                              href={resolvePublicFileUrl(msg.attachmentPath)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                src={resolvePublicFileUrl(msg.attachmentPath)}
                                alt={msg.attachmentName || "Attachment"}
                              />
                            </a>
                          ) : (
                            <a
                              className="mdd-chat-file"
                              href={resolvePublicFileUrl(msg.attachmentPath)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <LuPaperclip aria-hidden />
                              {msg.attachmentName || "Download attachment"}
                            </a>
                          )
                        ) : null}
                      </div>
                    </article>
                  );
                })
              )}
              <div ref={chatEndRef} />
            </div>

            <form className="mdd-composer" onSubmit={onSendMessage}>
              <textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder="Type your message..."
                rows={4}
              />
              <div className="mdd-composer__footer">
                <div className="mdd-composer__attach">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp"
                    hidden
                    onChange={(event) => setAttachment(event.target.files?.[0] || null)}
                  />
                  <button
                    type="button"
                    className="mdd-attach-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <LuPaperclip aria-hidden /> Attach File
                  </button>
                  {attachment ? <span className="mdd-attach-name">{attachment.name}</span> : null}
                </div>
                <button type="submit" className="mdd-btn mdd-btn--primary" disabled={sending}>
                  {sending ? "Sending..." : "Send Message"}
                </button>
              </div>
            </form>
          </section>
        </div>

        <aside className="mdd-side">
          <section className="mdd-card">
            <h2>Ticket Timeline</h2>
            <ol className="mdd-timeline">
              {TIMELINE_STEPS.map((step, index) => {
                const done = index < activeStep;
                const current = index === activeStep;
                const activity = activities.find((item) =>
                  String(item.title || "")
                    .toLowerCase()
                    .includes(step.label.toLowerCase().split(" ")[0]),
                );
                return (
                  <li
                    key={step.key}
                    className={`mdd-timeline__item${done ? " is-done" : ""}${current ? " is-current" : ""}`}
                  >
                    <span className="mdd-timeline__dot" aria-hidden>
                      {done || current ? "✓" : index + 1}
                    </span>
                    <div>
                      <strong>{step.label}</strong>
                      <small>
                        {current
                          ? formatDateTime(ticket.updatedAt)
                          : activity
                            ? formatDateTime(activity.createdAt)
                            : done
                              ? "Completed"
                              : "Pending"}
                      </small>
                    </div>
                  </li>
                );
              })}
            </ol>
            {activities.length > 0 ? (
              <button
                type="button"
                className="mdd-text-link"
                onClick={() => setShowAllActivity((value) => !value)}
              >
                {showAllActivity ? "Hide Activity" : "View All Activity"}
              </button>
            ) : null}
            {showAllActivity && activityItems.length > 0 ? (
              <ul className="mdd-activity-list">
                {activityItems.map((item) => (
                  <li key={item.id}>
                    <strong>{item.title}</strong>
                    <span>{item.remarks || item.performedByName || "—"}</span>
                    <small>{formatDateTime(item.createdAt)}</small>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>

          <section className="mdd-card">
            <h2>Support Information</h2>
            <div className="mdd-support">
              <span className="mdd-label">Assigned To</span>
              {ticket.assignedToName ? (
                <div className="mdd-person">
                  <span className="mdd-avatar mdd-avatar--support">
                    {nameInitials(ticket.assignedToName)}
                  </span>
                  <div>
                    <strong>{ticket.assignedToName}</strong>
                    <span>Support Executive</span>
                    <div className="mdd-support__actions">
                      {ticket.assignedToEmail ? (
                        <a
                          href={`mailto:${ticket.assignedToEmail}`}
                          className="mdd-icon-round"
                          title="Email"
                        >
                          <LuMail aria-hidden />
                        </a>
                      ) : null}
                      <button type="button" className="mdd-icon-round" title="Chat" onClick={focusComposer}>
                        <LuMessageSquare aria-hidden />
                      </button>
                      <button
                        type="button"
                        className="mdd-icon-round"
                        title="Phone"
                        onClick={() => toast.info("Please use chat or email to reach support.")}
                      >
                        <LuPhone aria-hidden />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="mdd-muted">Awaiting support assignment.</p>
              )}
              <div className="mdd-hours">
                <span className="mdd-label">Working Hours</span>
                <strong>Mon–Sat, 9:00 AM – 7:00 PM</strong>
              </div>
            </div>
          </section>

          <section className="mdd-card">
            <h2>Attachments ({attachments.length})</h2>
            {attachments.length === 0 ? (
              <p className="mdd-muted">No attachments yet.</p>
            ) : (
              <ul className="mdd-files">
                {visibleAttachments.map((file) => (
                  <li key={file.id}>
                    <span className="mdd-files__icon" aria-hidden>
                      {isImageMime(file.mime, file.name) ? <LuLayers /> : <LuFileText />}
                    </span>
                    <div>
                      <strong title={file.name}>{file.name}</strong>
                      <small>{formatDateTime(file.createdAt)}</small>
                    </div>
                    {file.path ? (
                      <a
                        href={resolvePublicFileUrl(file.path)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mdd-icon-round"
                        title="Download"
                      >
                        <LuDownload aria-hidden />
                      </a>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {attachments.length > 2 ? (
              <button
                type="button"
                className="mdd-text-link"
                onClick={() => setShowAllAttachments((value) => !value)}
              >
                {showAllAttachments ? "Show Less" : "View All Attachments"} <LuArrowRight aria-hidden />
              </button>
            ) : null}
          </section>
        </aside>
      </div>

      <section className="mdd-rating">
        <div>
          <strong>Help us improve our support</strong>
          <p>Rate your experience with this ticket resolution.</p>
        </div>
        <div className="mdd-stars" role="radiogroup" aria-label="Support rating">
          {[1, 2, 3, 4, 5].map((value) => (
            <button
              key={value}
              type="button"
              className={(hoverRating || rating) >= value ? "is-active" : ""}
              aria-label={`${value} star${value === 1 ? "" : "s"}`}
              onMouseEnter={() => setHoverRating(value)}
              onMouseLeave={() => setHoverRating(0)}
              onClick={() => {
                setRating(value);
                toast.success("Thanks for your feedback!");
              }}
            >
              <LuStar aria-hidden />
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
